import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast";
import RequisitionProductSelector from "../../components/CustomComponents/modal/RequisitionProductSelector";
import useDarkMode from "../../hooks/useDarkMode";
import RequisitionTabBar from "./requisitionTabBar";
import swal from "sweetalert";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import "./requisition-pages.css";

/** Parse SendRequisition validation message for Required/Available base units. */
function parseInsufficientStockMessage(message) {
  const msg = (message || "").replace(/\s+/g, " ").trim();
  const required = msg.match(/Required:\s*([\d.]+)\s*base units/i);
  const available = msg.match(/Available:\s*([\d.]+)\s*base units/i);
  return {
    required: required ? required[1] : null,
    available: available ? available[1] : null,
    fullMessage: msg,
  };
}

/** Remove internal "(stock checked at company ID…, product ID…)" suffix from user-facing detail text. */
function stripStockCheckIdsFromDetail(text) {
  return (text || "")
    .replace(/\s*\(stock checked at company ID:\s*\d+,\s*product ID:\s*\d+\)\.?/gi, "")
    .replace(/\s+/g, " ")
    .trim();
}

const UpdateRequisitionItems = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const compID = useSelector((state) => state.auth.user?.userData?.compID);
  const currentUserName = useSelector((state) => state.auth.user?.userData?.userName);
  const profileData = useSelector((state) => state.auth.profileData);
  const parentCompId = profileData?.parentComID ?? profileData?.ParentComID ?? 0;
  const isMain = !parentCompId || parseInt(parentCompId, 10) <= 0;
  const isDarkMode = useDarkMode();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [header, setHeader] = useState(null);
  const [lines, setLines] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [assignedProducts, setAssignedProducts] = useState([]);
  const [sendFailModal, setSendFailModal] = useState({
    open: false,
    message: "",
    errors: [],
  });

  // Product fetch uses requisition's requesting branch (whose products can be added). Never use compID as parent.
  const branchCompId = header
    ? (header?.requestingBranchCompID ?? header?.RequestingBranchCompID ?? compID)
    : compID;
  const headerParent = header?.parentCompID ?? header?.ParentCompID;
  const profileParent = parseInt(parentCompId, 10) || 0;
  const parentCompID = headerParent ?? (isMain ? compID : (profileParent || null));

  const fetchRequisition = useCallback(async () => {
    if (!id || !compID) return;
    try {
      setLoading(true);
      const res = await axiosInstance.get(APIs.GET_REQUISITION_BY_ID, {
        params: { requisitionId: id, compId: compID },
      });
      const data = res?.data?.data;
      const h = data?.header ?? data?.Header ?? data;
      const l = data?.lines ?? data?.Lines ?? [];
      if (h) setHeader(h);
      setLines(Array.isArray(l) ? l : []);
    } catch (e) {
      console.error(e);
      showErrorToast("Failed to load requisition.");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [id, compID]);

  const fetchAssignedProducts = useCallback(async () => {
    if (!branchCompId || !parentCompID || !compID) return;
    if (!isMain && (parentCompID === compID || parentCompID <= 0)) return;
    try {
      const res = await axiosInstance.get(APIs.GET_ASSIGNED_PRODUCTS_FOR_REQUISITION, {
        params: { branchCompId, parentCompId: parentCompID, viewerCompId: compID },
      });
      const d = res?.data?.data ?? res?.data ?? [];
      setAssignedProducts(Array.isArray(d) ? d : []);
    } catch (e) {
      console.warn(e);
      setAssignedProducts([]);
      showErrorToast(e?.response?.data?.message ?? "Could not load products for this branch.");
    }
  }, [branchCompId, parentCompID, compID, isMain]);

  useEffect(() => {
    fetchRequisition();
  }, [fetchRequisition]);

  useEffect(() => {
    if (header && branchCompId && parentCompID) fetchAssignedProducts();
  }, [header, branchCompId, parentCompID, fetchAssignedProducts]);

  useEffect(() => {
    if (showAddModal && header && branchCompId && parentCompID) fetchAssignedProducts();
  }, [showAddModal, header, branchCompId, parentCompID, fetchAssignedProducts]);

  const status = header?.status ?? header?.Status ?? "";
  const isDraft = status === "Draft";
  const isAccepted = status === "Accepted";
  const isSent = status === "Sent";

  const showSendErrorModal = (message, errors = []) => {
    setSendFailModal({
      open: true,
      message: message || "Send failed.",
      errors: Array.isArray(errors) ? errors : [],
    });
  };

  const closeSendFailModal = () =>
    setSendFailModal({ open: false, message: "", errors: [] });

  const handleSend = async () => {
    const sendLines = lines
      .map((l) => ({
        RequisitionLine_ID: l.requisitionLine_ID ?? l.RequisitionLine_ID,
        SendQty: parseFloat(l.sendQty ?? l.SendQty ?? 0) || 0,
      }))
      .filter((x) => x.SendQty > 0);
    if (sendLines.length === 0) {
      showErrorToast("Enter at least one Send qty.");
      return;
    }
    const ok = await swal({
      title: "Mark as Sent?",
      text: "Stock will be deducted from main.",
      icon: "warning",
      buttons: { cancel: "No", confirm: "Yes" },
      dangerMode: true,
    });
    if (!ok) return;
    try {
      setLoading(true);
      const res = await axiosInstance.post(APIs.POST_SEND_REQUISITION, {
        RequisitionId: parseInt(id, 10),
        CompId: compID,
        SentBy: currentUserName || "System",
        Lines: sendLines,
      });
      if (!res?.data?.status) {
        showSendErrorModal(
          res?.data?.message ?? "Send failed.",
          res?.data?.errors ?? []
        );
        return;
      }
      showSuccessToast("Requisition sent. Stock deducted from main.");
      fetchRequisition();
    } catch (e) {
      const data = e?.response?.data;
      showSendErrorModal(
        data?.message ?? e?.message ?? "Send failed.",
        data?.errors ?? []
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReceive = async () => {
    const receiveLines = lines
      .map((l) => ({
        RequisitionLine_ID: l.requisitionLine_ID ?? l.RequisitionLine_ID,
        ReceivedQty:
          parseFloat(l.receivedQty ?? l.ReceivedQty ?? 0) ||
          parseFloat(l.sendQty ?? l.SendQty ?? 0) ||
          0,
      }))
      .filter((x) => x.ReceivedQty > 0);
    if (receiveLines.length === 0) {
      showErrorToast("Enter at least one Received qty or Send qty.");
      return;
    }
    const ok = await swal({
      title: "Confirm Receive?",
      text: "Stock will be added to branch.",
      icon: "warning",
      buttons: { cancel: "No", confirm: "Yes" },
      dangerMode: true,
    });
    if (!ok) return;
    try {
      setLoading(true);
      await axiosInstance.post(APIs.POST_RECEIVE_REQUISITION, {
        RequisitionId: parseInt(id, 10),
        CompId: compID,
        ReceivedBy: currentUserName || "System",
        ReceiptDate: new Date().toISOString(),
        Lines: receiveLines,
      });
      showSuccessToast("Requisition received. Stock added to branch.");
      fetchRequisition();
    } catch (e) {
      showErrorToast(e?.response?.data?.message ?? "Receive failed.");
    } finally {
      setLoading(false);
    }
  };

  const renderBreadcrumb = () => (
    <div className="d-flex align-items-center mb-3 flex-wrap gap-2">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to="/dashboard">Home</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/requisition/manageRequisitions">Requisitions</Link>
          </li>
          <li className="breadcrumb-item active">Items</li>
        </ol>
        <h1 className="page-header mb-0">
          {header
            ? `${header.requisition_Number ?? header.Requisition_Number ?? "REQ-" + id} — Items`
            : "Requisition items"}
        </h1>
      </div>
      <div className="ms-auto req-toolbar">
        {(isDraft || (isAccepted && isMain)) && (
          <button type="button" onClick={() => setShowAddModal(true)} className="btn req-btn req-btn-primary">
            <i className="fa-solid fa-plus" />
            Add products
          </button>
        )}
        {isAccepted && isMain && (
          <button type="button" onClick={handleSend} className="btn req-btn req-btn-success">
            <i className="fas fa-paper-plane" />
            Mark as sent
          </button>
        )}
        {isSent && (
          <button type="button" onClick={handleReceive} className="btn req-btn req-btn-success">
            <i className="fas fa-check-circle" />
            Confirm receive
          </button>
        )}
        <Link to={`/requisition/updateRequisition/${id}`} className="btn req-btn req-btn-outline-secondary">
          General
        </Link>
      </div>
    </div>
  );

  if (initialLoading) {
    return (
      <div className={`requisition-page ${isDarkMode ? "requisition-page--dark" : ""}`}>
        <Preloader />
      </div>
    );
  }

  const sendFailRows = sendFailModal.errors.map((e, idx) => {
    const productName = (e.productName ?? e.ProductName ?? "").trim();
    const raw = (e.message ?? e.Message ?? "").replace(/\n/g, " ");
    const p = parseInsufficientStockMessage(raw);
    return {
      key: idx,
      productName: productName || "—",
      required: p.required ?? "—",
      available: p.available ?? "—",
      detail: stripStockCheckIdsFromDetail(p.fullMessage || raw || "Insufficient stock"),
    };
  });

  return (
    <div className={`requisition-page ${isDarkMode ? "requisition-page--dark" : ""}`}>
      {loading && <Preloader />}
      <CustomModal
        isOpen={sendFailModal.open}
        onClose={closeSendFailModal}
        title="Send Failed"
        subtitle={sendFailModal.message}
        modalSize="modal-lg"
        maxHeight="90vh"
        minHeight="280px"
        isCentered
        showButton
        showSubmitButton
        showCancelButton={false}
        submitButtonText="OK"
        onSubmit={closeSendFailModal}
        zIndex={10050}
      >
        {sendFailRows.length > 0 ? (
          <div
            className="table-responsive rounded border"
            style={{
              maxHeight: "min(55vh, 420px)",
              overflow: "auto",
              borderColor: isDarkMode ? "#495057" : "#dee2e6",
            }}
          >
            <table
              className={`table table-hover table-sm mb-0 ${isDarkMode ? "table-dark" : ""}`}
              style={{ fontSize: "13px" }}
            >
              <thead
                className={isDarkMode ? "" : "table-light"}
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: isDarkMode ? "#343a40" : "#f8f9fa",
                }}
              >
                <tr>
                  <th className="pt-2 pb-2">Product name</th>
                  <th className="pt-2 pb-2 text-end">Required (base units)</th>
                  <th className="pt-2 pb-2 text-end">Available (base units)</th>
                  <th className="pt-2 pb-2">Details</th>
                </tr>
              </thead>
              <tbody>
                {sendFailRows.map((row) => (
                  <tr key={row.key}>
                    <td className="align-middle">{row.productName}</td>
                    <td className="align-middle text-end">{row.required}</td>
                    <td className="align-middle text-end">{row.available}</td>
                    <td className="align-middle small text-muted" style={{ maxWidth: "360px" }}>
                      {row.detail}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="mb-0 text-muted small">{sendFailModal.message}</p>
        )}
      </CustomModal>
      {renderBreadcrumb()}
      {id && <RequisitionTabBar id={id} />}

      <div className="card border-0" style={{ position: "relative", zIndex: 1 }}>
        <div className="tab-content p-3">
          <div className="tab-pane fade show active">
            <Panel className="mb-0 border-0 shadow-none">
              <PanelHeader noButton className="req-panel-header">
                Line items
                {header && (
                  <span className="ms-2 opacity-90 fw-normal" style={{ fontSize: "14px" }}>
                    · {status}
                  </span>
                )}
              </PanelHeader>
              <PanelBody className="req-panel-body">
          <RequisitionProductSelector
            lines={lines}
            setLines={setLines}
            header={header}
            requisitionId={id}
            compID={compID}
            branchCompId={branchCompId}
            parentCompID={parentCompID}
            isMain={isMain}
            products={assignedProducts}
            onRefresh={fetchRequisition}
            setLoading={setLoading}
            showAddModal={showAddModal}
            setShowAddModal={setShowAddModal}
            onSend={handleSend}
            onReceive={handleReceive}
          />
              </PanelBody>
            </Panel>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateRequisitionItems;
